import { useMutation, useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

// Wire-format mirrors src/flowstack/server/routes/affdo.py. P1.3a
// surfaces per-fragment + per-dihedral parameter-quality data from
// the AFFDO torsion-refinement output the platform's typed-object
// catalog already persists.

/** Three-state quality label the GUI uses to color a fragment row. */
export type FragmentStatus = "ok" | "partial" | "failed";

export interface FragmentSummary {
  torsion_refinement_result_id: string;
  fragment_id: string;
  torsion_set_id: string;
  n_fitted: number;
  n_failed: number;
  n_total: number;
  max_fit_rmsd_kcal_per_mol: number | null;
  mean_fit_rmsd_kcal_per_mol: number | null;
  status: FragmentStatus;
}

export interface FragmentSummaryListResponse {
  run_id: string;
  fragments: FragmentSummary[];
}

/**
 * AFFDO's own word for what happened to a torsion.
 *
 * Kept as a widened `string` on the wire because it is a PERSISTED
 * vocabulary: the server projects whatever the artifact recorded, and a
 * value added upstream must not make an already-deployed portal
 * unparseable. Use `isGaffRetained` rather than switching on it exhaustively.
 * `null` on a record written before the runner stamped it — unknown, not good.
 *
 * The band words AFFDO actually writes today are `high` / `medium` / `poor`
 * (it prints a NOTE for `medium`; `poor` falls back to standard GAFF2), but
 * the type stays widened on purpose — see above.
 * See `docs/source/dev/reference/torsion-fit-cascade.rst`.
 */
export type AffdoQuality = string | null;

/**
 * The three quality fields every per-torsion row carries.
 *
 * `fit_norm_rmse` / `gaff_norm_rmse` are AFFDO's **dimensionless** norm_RMSE
 * — the number every gate in the fit cascade actually reads. They are NOT
 * energies: never render them with a kcal/mol suffix. The kcal/mol number is
 * the separate `fit_rmsd_kcal_per_mol`, a DIFFERENT quantity and not a
 * rescaling of these. See
 * `docs/source/dev/reference/torsion-fit-cascade.rst`.
 *
 * `affdo_quality` is named for what it is (#882): the band AFFDO recorded.
 * On a `gaff_sufficient` torsion that is the PRE-fit gate's verdict about
 * plain GAFF2, not a judgement about a fit that never ran — read it beside
 * `fit_status`, never label it "fit quality" unconditionally.
 */
export interface TorsionQualityFields {
  affdo_quality: AffdoQuality;
  fit_norm_rmse: number | null;
  gaff_norm_rmse: number | null;
}

export interface FittedTorsionRow extends TorsionQualityFields {
  torsion_id: string;
  atoms_in_fragment: number[];
  fit_rmsd_kcal_per_mol: number;
  fit_method: string;
}

export interface FailedTorsionRow extends TorsionQualityFields {
  torsion_id: string;
  atoms_in_fragment: number[];
  /**
   * The canonical `FitFailReason` literal — a *narrowing* of what AFFDO
   * recorded. A `no_improvement` verdict arrives here as `nonconvergence`,
   * which reads like a crash when in fact the fit converged and lost to
   * plain GAFF2. Show `affdo_quality` beside it.
   */
  fail_reason: string;
  fit_rmsd_kcal_per_mol: number | null;
}

/**
 * The `fit_status` values that mean "the force field kept standard GAFF2".
 *
 * Each is a recorded DECISION by AFFDO: the pre-fit gate found GAFF2 already
 * sufficient (`gaff_sufficient`), or the fit ran and was rejected in favour of
 * GAFF2 (`affdo_worse_than_gaff`, `no_improvement`).
 */
const GAFF_RETAINED_STATUSES: ReadonlySet<string> = new Set([
  "gaff_sufficient",
  "affdo_worse_than_gaff",
  "no_improvement",
]);

/**
 * True only when the artifact POSITIVELY says GAFF2 was retained (#869/#878).
 *
 * **Never infer this from the absence of `fitted_relative_kcal_per_mol`.** The
 * curve is also missing when the artifact predates the fit-profile block, when
 * the driver could not read `energies.json`, when the runner's length-mismatch
 * fallback dropped the block, and when the route could not join the overlay.
 * Reading absence as a decision turns every one of those gaps into the claim
 * "this torsion's refitted parameters were discarded" — about torsions whose
 * refitted parameters are in fact in the shipped force field.
 *
 * `fit_status` is unset on artifacts written before it existed, and that
 * correctly yields `false` here: unknown is not a decision.
 */
export function isGaffRetained(profile: DihedralProfile): boolean {
  const status = profile.fit_status;
  return typeof status === "string" && GAFF_RETAINED_STATUSES.has(status);
}

/**
 * Plain-language rendering of `affdo_quality`, for the panel caption.
 *
 * Only the values AFFDO actually writes are named; anything else falls
 * through to the raw token rather than being dropped, so a vocabulary added
 * upstream shows up as itself instead of disappearing from the UI.
 */
export function describeAffdoQuality(quality: AffdoQuality): string | null {
  switch (quality) {
    case null:
      return null;
    case "not_needed":
      return "GAFF2 already within tolerance — not refit";
    case "no_improvement":
      return "fit converged but did not beat GAFF2";
    case "poor":
      return "fit quality below the acceptance band";
    case "high":
      return "high-quality fit";
    case "medium":
      return "medium-quality fit";
    case "none":
    case "unknown":
      return null;
    default:
      return quality;
  }
}

/**
 * Decimal places for EVERY fit-RMSD figure in the app (kcal/mol).
 *
 * One number, in one place, because the same metric was being printed to three
 * places in the tiles and the panel headers and to two on the failed panel's
 * status line — so `1.900` and `1.90` were the same measurement wearing two
 * faces, and a reader comparing them had to work out that nothing had changed.
 *
 * **Three, not two**, for the reason O0.1 recorded when under-precision printed
 * a real signal as `0.00000`: the acceptance band is 1.0 kcal/mol and fits
 * cluster well under it, so the third decimal is where two adequate fits
 * actually differ. Rounding it away costs information; carrying it costs a
 * character.
 */
export const RMSD_DECIMALS = 3;

/** A fit RMSD in kcal/mol at the app's one precision — see `RMSD_DECIMALS`. */
export function formatRmsdKcal(value: number): string {
  return value.toFixed(RMSD_DECIMALS);
}

export interface FragmentDihedralsResponse {
  torsion_refinement_result_id: string;
  fragment_id: string;
  fitted: FittedTorsionRow[];
  failed: FailedTorsionRow[];
}

/**
 * One torsion's profile, ready to plot.
 *
 * Every series is **relative** — min-subtracted, never an absolute energy.
 * A QM total energy and a GAFF2 evaluation share no zero (roughly -6e4 vs
 * -6e2 kcal/mol), so the raw magnitudes cannot go on one axis; the names say
 * `relative` so nobody re-derives that the hard way.
 */
export interface DihedralProfile extends Partial<TorsionQualityFields> {
  torsion_id: string;
  angles_deg: number[];
  qm_relative_kcal_per_mol: number[];
  mm_initial_relative_kcal_per_mol: number[];
  /** The floors the server subtracted, so the absolute scale stays auditable.
   * Not plotted, and not currently read by the chart — they exist for API
   * consumers. See `docs/source/dev/reference/affdo-quality-projection.rst`,
   * which is the authoritative statement; do not restate it here.
   *
   * Both are `null` on the FIT-overlay path: there the runner min-subtracted
   * upstream and the floors it removed are not in the artifact, so the server
   * reports None rather than inventing a 0.0. */
  qm_zero_kcal_per_mol?: number | null;
  mm_initial_zero_kcal_per_mol?: number | null;
  /** The force field `mm_profile_initial` was evaluated with, verbatim from
   * the artifact. The dashed series is labelled from THIS, never from a
   * hard-coded "GAFF2" — the base force field is a value the artifact
   * declares, and asserting it in the presentation layer is the same unbacked
   * claim about a stored quantity that the unit handling exists to prevent. */
  mm_initial_force_field?: string | null;
  /**
   * The post-fit MM profile — the third curve, and the one that answers
   * "did the fit actually beat GAFF?" (#869).
   *
   * `null` for a torsion that was never fitted (AFFDO's pre-fit gate found
   * standard GAFF2 already sufficient) or whose fit was REJECTED. In both
   * cases standard GAFF2 is what ends up in the force field, and the panel
   * says so rather than rendering blank.
   *
   * When present, the server has already replaced `angles_deg` and the two
   * lists above with the grid the FIT ran on — the post-drop, renumbered
   * one — so all four arrays are index-aligned.
   */
  fitted_relative_kcal_per_mol?: number[] | null;
  /** kcal/mol, and a DIFFERENT quantity from `fit_norm_rmse` — not a
   * rescaling of it. Present only on the fit-overlay path. */
  fit_rmsd_kcal_per_mol?: number | null;
  /** `fitted` | `gaff_sufficient` | `affdo_worse_than_gaff` | `no_improvement`.
   *
   * This is the ONLY field that licenses a "standard GAFF2 retained" claim.
   * Absence of `fitted_relative_kcal_per_mol` does not — see `isGaffRetained`. */
  fit_status?: string | null;
}

export interface FragmentProfilesResponse {
  fragment_id: string;
  profiles: DihedralProfile[];
}

export interface SkippedDihedralRow {
  fragment_id: string;
  torsion_id: string;
  atoms_in_fragment: number[];
  fail_reason: string;
  fit_rmsd_kcal_per_mol: number | null;
}

export interface SkippedDihedralsResponse {
  run_id: string;
  skipped: SkippedDihedralRow[];
}

export interface MethodRecommendationRequest {
  n_heavy_atoms: number;
  formal_charge: number;
  has_aromatic_ring: boolean;
  available_plugins: string[];
}

export interface MethodAlternative {
  charge_model: string;
  base_forcefield: string;
  qm_method: string | null;
  rationale: string;
}

export interface MethodRecommendationResponse {
  charge_model: string;
  base_forcefield: string;
  qm_method: string | null;
  confidence: number;
  rationale: string;
  alternatives: MethodAlternative[];
}

/**
 * `refreshKey` — an optional PROGRESS fingerprint appended to the key.
 *
 * These projections are computed from whatever artifacts exist at request
 * time, so on a live run they go stale the moment a new `torsion_fit` child
 * lands. Giving them their own `refetchInterval` would be the second polling
 * loop flowstack#950 rules out; folding the run view's existing DAG-poll
 * fingerprint into the key refreshes them on the poll that is already
 * happening (Rupert PR-955 r0). Omitted by a caller that wants the plain
 * cached read, in which case the key is exactly what it was before.
 */
export function runFragmentsQueryKey(runId: string, refreshKey?: string): readonly unknown[] {
  return ["runs", runId, "affdo", "fragments", refreshKey ?? null] as const;
}

export function fragmentDihedralsQueryKey(
  runId: string,
  fragmentId: string,
  refreshKey?: string
): readonly unknown[] {
  return [
    "runs",
    runId,
    "affdo",
    "fragments",
    fragmentId,
    "dihedrals",
    refreshKey ?? null,
  ] as const;
}

export function fragmentProfilesQueryKey(
  runId: string,
  fragmentId: string,
  refreshKey?: string
): readonly unknown[] {
  return ["runs", runId, "affdo", "fragments", fragmentId, "profiles", refreshKey ?? null] as const;
}

/** Per-fragment summary for a run. ``enabled`` defaults to true when
 * ``runId`` is non-empty so callers can omit the options bag for
 * the canonical case. */
export function useRunFragments(
  runId: string,
  options?: { enabled?: boolean; refreshKey?: string }
) {
  const enabled = (options?.enabled ?? true) && Boolean(runId);
  return useQuery({
    queryKey: runFragmentsQueryKey(runId, options?.refreshKey),
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<FragmentSummaryListResponse> =>
      apiFetch<FragmentSummaryListResponse>(
        `/api/runs/${encodeURIComponent(runId)}/affdo/fragments`
      ),
    enabled,
    staleTime: 30_000,
  });
}

/** Per-torsion table for one fragment of one run. */
export function useFragmentDihedrals(
  runId: string,
  fragmentId: string,
  options?: { enabled?: boolean; refreshKey?: string }
) {
  const enabled = (options?.enabled ?? true) && Boolean(runId) && Boolean(fragmentId);
  return useQuery({
    queryKey: fragmentDihedralsQueryKey(runId, fragmentId, options?.refreshKey),
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<FragmentDihedralsResponse> =>
      apiFetch<FragmentDihedralsResponse>(
        `/api/runs/${encodeURIComponent(runId)}/affdo/fragments/${encodeURIComponent(fragmentId)}/dihedrals`
      ),
    enabled,
    staleTime: 30_000,
  });
}

export function runSkippedQueryKey(runId: string): readonly unknown[] {
  return ["runs", runId, "affdo", "skipped"] as const;
}

/** Aggregated skipped-dihedrals list for the run. The
 * SkippedDihedralsList consumes this; the per-fragment small-multiples
 * already surface ghost panels inline. */
export function useRunSkipped(runId: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && Boolean(runId);
  return useQuery({
    queryKey: runSkippedQueryKey(runId),
    queryFn: async (): Promise<SkippedDihedralsResponse> =>
      apiFetch<SkippedDihedralsResponse>(`/api/runs/${encodeURIComponent(runId)}/affdo/skipped`),
    enabled,
    staleTime: 30_000,
  });
}

/** Method-selection mutation. Returns a recommendation card the GUI
 * animates in with a confidence arc + 2-3 line rationale. */
export function useRecommendMethod() {
  return useMutation({
    mutationFn: async (body: MethodRecommendationRequest): Promise<MethodRecommendationResponse> =>
      apiFetch<MethodRecommendationResponse>("/api/affdo/recommend-method", {
        method: "POST",
        json: body,
      }),
  });
}

/** Per-dihedral QM + MM-initial scan profiles for one fragment. The
 * DihedralRMSDGrid joins this on torsion_id with useFragmentDihedrals
 * to render the QM solid + MM dashed curves alongside the RMSD bar. */
export function useFragmentProfiles(
  runId: string,
  fragmentId: string,
  options?: { enabled?: boolean; refreshKey?: string }
) {
  const enabled = (options?.enabled ?? true) && Boolean(runId) && Boolean(fragmentId);
  return useQuery({
    queryKey: fragmentProfilesQueryKey(runId, fragmentId, options?.refreshKey),
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<FragmentProfilesResponse> =>
      apiFetch<FragmentProfilesResponse>(
        `/api/runs/${encodeURIComponent(runId)}/affdo/fragments/${encodeURIComponent(fragmentId)}/profiles`
      ),
    enabled,
    staleTime: 60_000,
  });
}
